/**
 * Age ↔ classId mapping for Indonesian PAUD/TK.
 *
 * Per data-model doc 03 §3 + UX convention:
 *   - Usia 2 tahun   → PG  (Play Group, classId=1)
 *   - Usia 3-4 tahun → TK A (Taman Kanak-kanak A, classId=2)
 *   - Usia 5-6 tahun → TK B (Taman Kanak-kanak B, classId=3)
 *
 * Why a single source of truth:
 *  - The form picks age only (2-6). The server requires classId (1-3).
 *  - The daily algorithm uses both: age for difficulty bias, classId for
 *    grade-level vocabulary. We compute classId here so the server stays
 *    a single source for content but the client picks age.
 *  - This file is a duplicate of the server's ageToClassId in v1-quiz.ts
 *    (kept in sync). Server still validates via childCreateSchema so a
 *    drift here fails loudly with a 400 BadRequest, not silent corruption.
 */

import type { AgeBracket, ClassId } from '@funpaper/shared';

export const AGE_BRACKETS: readonly AgeBracket[] = [2, 3, 4, 5, 6];

export function ageToClassId(age: AgeBracket): ClassId {
  if (age === 2) return 1; // PG
  if (age === 3 || age === 4) return 2; // TK A
  return 3; // TK B (age 5 or 6)
}

export function classIdToLabel(classId: ClassId): string {
  switch (classId) {
    case 1:
      return 'PG';
    case 2:
      return 'TK A';
    case 3:
      return 'TK B';
  }
}

// ----------------------------------------------------------------------------
// Plan 21 — preferensi umur (section Umur di edit-preferensi)
// ----------------------------------------------------------------------------

/** Opsi section Umur: 3 kelompok usia + label rentang usia. */
export const CLASS_OPTIONS: readonly {
  classId: ClassId;
  label: string;
  ageLabel: string;
}[] = [
  { classId: 1, label: 'PG', ageLabel: '2 tahun' },
  { classId: 2, label: 'TK A', ageLabel: '3–4 tahun' },
  { classId: 3, label: 'TK B', ageLabel: '5–6 tahun' },
];

/**
 * Payload PUT preferredClassId (plan 21): kelas terpilih yang SAMA dengan
 * kelas profil anak dikirim sebagai null (ikuti profil) supaya perubahan
 * usia profil anak otomatis terpakai tanpa edit preferensi lagi.
 */
export function preferredClassPayload(
  selected: ClassId,
  profileClassId: ClassId,
): ClassId | null {
  return selected === profileClassId ? null : selected;
}
